import { getContentEmailSignature } from '@/utils/translations.ts';

const stripSignatureWrapper = (signature) => {
    if (!signature) {
        return '';
    }

    const html = $('<div />', {
        html: signature,
    });

    const wrappedSignature = html.find('[data-composer-signature="true"], div.signature').first();

    if (!wrappedSignature.length) {
        return signature;
    }

    return wrappedSignature.html();
};

/**
 * Adds one comparison space so compact editor HTML and formatted signature HTML match.
 */
const appendTextBoundary = (parts) => {
    if (parts.length && parts.at(-1) !== ' ') {
        parts.push(' ');
    }
};

const isHiddenElement = (element) => {
    const display = element.ownerDocument.defaultView?.getComputedStyle(element).display || '';

    return display === 'none';
};

/**
 * Returns true when an element's rendered layout should count as a space in signature matching.
 */
const createsTextBoundary = (element) => {
    if (element.tagName === 'BR') {
        return true;
    }

    const display = element.ownerDocument.defaultView?.getComputedStyle(element).display || '';

    return Boolean(display) && !display.startsWith('inline') && display !== 'contents';
};

/**
 * Builds signature text for comparison when Tiptap has removed source whitespace between blocks.
 */
export const walkNode = (node, parts) => {
    if (node.nodeType === Node.TEXT_NODE) {
        parts.push(node.textContent || '');

        return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) {
        return;
    }

    if (isHiddenElement(node)) {
        return;
    }

    const hasTextBoundary = createsTextBoundary(node);

    if (hasTextBoundary) {
        appendTextBoundary(parts);
    }

    for (const childNode of node.childNodes) {
        walkNode(childNode, parts);
    }

    if (hasTextBoundary) {
        appendTextBoundary(parts);
    }
};

const normalizeHtmlText = (htmlContent) => {
    if (!htmlContent) {
        return '';
    }

    const container = document.createElement('div');
    const parts = [];

    container.innerHTML = htmlContent;

    walkNode(container, parts);

    return parts.join('').replaceAll(/\s+/g, ' ').trim();
};

/**
 * Reads signature image URLs because image-only signatures cannot be detected by text comparison.
 */

// CloudFront/S3 signed URL parameters that rotate on every page load.
// Strip them so the same image compares equal across URL refreshes.
const SIGNED_IMAGE_QUERY_PARAMETERS = new Set(['expires', 'key-pair-id', 'policy', 'signature']);

const isSignedImageQueryParameter = (parameter) => {
    const normalizedParameter = parameter.toLowerCase();

    return (
        SIGNED_IMAGE_QUERY_PARAMETERS.has(normalizedParameter) ||
        normalizedParameter.startsWith('x-amz-')
    );
};

/** Strip volatile signing parameters so the same image compares equal across URL refreshes. */
const normalizeImageSource = (source) => {
    if (!source) {
        return '';
    }

    const trimmedSource = source.trim();

    if (!trimmedSource || trimmedSource.startsWith('data:') || trimmedSource.startsWith('cid:')) {
        return trimmedSource;
    }

    try {
        const url = new URL(trimmedSource, globalThis.location.origin);
        const hasProtocol = /^[a-z][a-z\d+.-]*:/i.test(trimmedSource);
        const isProtocolRelative = trimmedSource.startsWith('//');

        for (const parameter of Array.from(url.searchParams.keys())) {
            if (isSignedImageQueryParameter(parameter)) {
                url.searchParams.delete(parameter);
            }
        }

        const normalizedSource = `${url.pathname}${url.search}${url.hash}`;

        return hasProtocol || isProtocolRelative
            ? `${url.origin}${normalizedSource}`
            : normalizedSource;
    } catch {
        return trimmedSource;
    }
};

const getImageSource = (image) => image.getAttribute('src')?.trim();

const getComparableImageSource = (image) => normalizeImageSource(getImageSource(image));

const getImageSources = (htmlContent) => {
    if (!htmlContent) {
        return [];
    }

    const container = document.createElement('div');
    container.innerHTML = htmlContent;

    return Array.from(container.querySelectorAll('img')).map(getImageSource).filter(Boolean);
};

const isElementOrAncestorHidden = (element) => {
    let currentElement = element;

    while (currentElement) {
        if (isHiddenElement(currentElement)) {
            return true;
        }

        currentElement = currentElement.parentElement;
    }

    return false;
};

const getFollowingNodes = (node, rootNode) => {
    const nodes = [];
    let cursor = node;

    while (cursor && cursor !== rootNode) {
        if (cursor.nextSibling) {
            cursor = cursor.nextSibling;
            nodes.push(cursor);

            continue;
        }

        cursor = cursor.parentElement;
    }

    return nodes;
};

/** Collect sibling nodes between two nodes without including either endpoint. */
const getNodesBetween = (startNode, endNode, rootNode) => {
    const nodes = [];

    for (const node of getFollowingNodes(startNode, rootNode)) {
        if (node === endNode || node.contains?.(endNode)) {
            break;
        }

        nodes.push(node);
    }

    return nodes;
};

const getVisibleText = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent || '';
    }

    if (node.nodeType === Node.DOCUMENT_FRAGMENT_NODE) {
        return Array.from(node.childNodes).map(getVisibleText).join('');
    }

    if (node.nodeType !== Node.ELEMENT_NODE || isHiddenElement(node)) {
        return '';
    }

    return Array.from(node.childNodes).map(getVisibleText).join('');
};

/** Return true if this node marks a visual boundary (e.g. <hr>) where signature content ends. */
const isNonSignatureContentBoundary = (node) => {
    if (node.nodeType !== Node.ELEMENT_NODE) {
        return false;
    }

    return node.tagName === 'HR' || Boolean(node.querySelector?.('hr'));
};

// Strings that identify footer content (unsubscribe link, quoted reply) below the signature.
// Tiptap strips the proper data attributes from their wrappers, but these fragments survive
// in the HTML. Used to tell "text after images is just footer, not email body."
const COMPOSER_SUFFIX_MARKERS = [
    '%tag_unsubscribe_url%',
    'data-composer-unsubscribe',
    'data-composer-original-communication',
    'original-communication',
    'unsubscribe-link',
];

const getNodeHtml = (node) => node.outerHTML || node.textContent || '';

const includesComposerSuffixMarker = (node) => {
    const html = getNodeHtml(node);

    return COMPOSER_SUFFIX_MARKERS.some((marker) => html.includes(marker));
};

const nodesIncludeComposerSuffixMarker = (nodes) => nodes.some(includesComposerSuffixMarker);

/** Walk siblings after node and return the first that looks like footer content (or null). */
const findNormalizedComposerSuffixStartNode = (node, rootNode) => {
    const followingNodes = getFollowingNodes(node, rootNode);

    for (const [index, followingNode] of followingNodes.entries()) {
        if (includesComposerSuffixMarker(followingNode)) {
            return followingNode;
        }

        if (isNonSignatureContentBoundary(followingNode)) {
            const suffixNodes = followingNodes.slice(index);
            const visibleSuffixText = suffixNodes.map(getVisibleText).join('').trim();

            if (!visibleSuffixText || nodesIncludeComposerSuffixMarker(suffixNodes)) {
                return followingNode;
            }
        }

        if (getVisibleText(followingNode).trim()) {
            return null;
        }
    }

    return null;
};

/**
 * Finds generated signature images at the editable body tail, including signed URL refreshes.
 */
const findSignatureMediaTailMatch = (body, signature) => {
    const signatureImageSources = getImageSources(signature).map(normalizeImageSource);

    if (!signatureImageSources.length) {
        return null;
    }

    const container = document.createElement('div');
    container.innerHTML = body;

    const bodyImages = Array.from(container.querySelectorAll('img')).filter(
        (image) => !isElementOrAncestorHidden(image)
    );

    let suffixStartNode = null;
    const image = bodyImages.find((bodyImage, imageIndex) => {
        const candidateImageSources = bodyImages
            .slice(imageIndex, imageIndex + signatureImageSources.length)
            .map(getComparableImageSource);
        const lastSignatureImage = bodyImages[imageIndex + signatureImageSources.length - 1];
        const hasSignatureImageSequence = signatureImageSources.every(
            (source, sourceIndex) => candidateImageSources[sourceIndex] === source
        );

        if (!hasSignatureImageSequence) {
            return false;
        }

        const visibleTextBetweenSignatureMedia =
            bodyImage === lastSignatureImage
                ? ''
                : getNodesBetween(bodyImage, lastSignatureImage, container)
                      .map(getVisibleText)
                      .join('')
                      .trim();

        if (visibleTextBetweenSignatureMedia) {
            return false;
        }

        const visibleTextAfterSignatureMedia = getFollowingNodes(lastSignatureImage, container)
            .map(getVisibleText)
            .join('')
            .trim();

        if (!visibleTextAfterSignatureMedia) {
            return true;
        }

        suffixStartNode = findNormalizedComposerSuffixStartNode(lastSignatureImage, container);

        return Boolean(suffixStartNode);
    });

    if (!image) {
        return null;
    }

    return {
        container,
        image,
        suffixStartNode,
    };
};

/**
 * Checks whether the editable body already ends with the generated signature images.
 */
const bodyIncludesSignatureMedia = (body, signature) => {
    return Boolean(findSignatureMediaTailMatch(body, signature));
};

/** Walk up from node to find its direct child of container. */
const getTopLevelNodeContaining = (container, node) => {
    let currentNode = node;

    while (currentNode.parentNode && currentNode.parentNode !== container) {
        currentNode = currentNode.parentNode;
    }

    return currentNode.parentNode === container ? currentNode : null;
};

const getVisibleTextBeforeNode = (rootNode, node) => {
    const range = rootNode.ownerDocument.createRange();

    range.setStart(rootNode, 0);
    range.setEndBefore(node);

    return getVisibleText(range.cloneContents()).trim();
};

const getLeadingImageSources = (htmlContent) => {
    const container = document.createElement('div');
    container.innerHTML = htmlContent;

    return Array.from(container.querySelectorAll('img'))
        .filter(
            (image) =>
                !isElementOrAncestorHidden(image) && !getVisibleTextBeforeNode(container, image)
        )
        .map(getComparableImageSource)
        .filter(Boolean);
};

/** Include signature images immediately before a match that was found using text only. */
const includeLeadingSignatureImages = (nodes, startIndex, signature) => {
    const signatureImageSources = getLeadingImageSources(stripSignatureWrapper(signature));

    if (
        !signatureImageSources.length ||
        getLeadingImageSources(getNodeHtml(nodes[startIndex])).length
    ) {
        return startIndex;
    }

    const candidateImageSources = [];

    for (let index = startIndex - 1; index >= 0; index -= 1) {
        const html = getNodeHtml(nodes[index]);

        if (normalizeHtmlText(html) || isNonSignatureContentBoundary(nodes[index])) {
            break;
        }

        candidateImageSources.unshift(...getImageSources(html).map(normalizeImageSource));

        if (candidateImageSources.length > signatureImageSources.length) {
            break;
        }

        if (
            candidateImageSources.length === signatureImageSources.length &&
            signatureImageSources.every(
                (source, imageIndex) => source === candidateImageSources[imageIndex]
            )
        ) {
            return index;
        }
    }

    return startIndex;
};

/** Find the top-level node where the signature images begin (may be the image itself or a wrapper). */
const getSignatureMediaStartNode = (container, image) => {
    const topLevelNode = getTopLevelNodeContaining(container, image);

    if (!topLevelNode) {
        return image;
    }

    if (topLevelNode === image) {
        return topLevelNode;
    }

    return getVisibleTextBeforeNode(topLevelNode, image) ? image : topLevelNode;
};

/** Mutates container by deleting everything from node onward. */
const removeTailFromNode = (container, node) => {
    const range = container.ownerDocument.createRange();

    range.setStartBefore(node);
    range.setEndAfter(container.lastChild);
    range.deleteContents();

    return container.innerHTML;
};

const getHtmlBeforeNode = (container, node) => {
    const range = container.ownerDocument.createRange();
    const htmlContainer = document.createElement('div');

    range.setStart(container, 0);
    range.setEndBefore(node);
    htmlContainer.appendChild(range.cloneContents());

    return htmlContainer.innerHTML;
};

const getHtmlFromNode = (container, node) => {
    const range = container.ownerDocument.createRange();
    const htmlContainer = document.createElement('div');

    range.setStartBefore(node);
    range.setEndAfter(container.lastChild);
    htmlContainer.appendChild(range.cloneContents());

    return htmlContainer.innerHTML;
};

/** Replace an existing image signature (detected via media tail match) with the fresh one. */
const replaceSignatureUsingMediaTailMatch = ({ body, signature }) => {
    const { bodyWithoutSuffix, suffix } = splitBodyAtSuffix(body);
    const match = findSignatureMediaTailMatch(bodyWithoutSuffix, signature);

    if (!match) {
        return body;
    }

    const signatureStartNode = getSignatureMediaStartNode(match.container, match.image);
    const prefixHtml = match.suffixStartNode
        ? getHtmlBeforeNode(match.container, signatureStartNode)
        : removeTailFromNode(match.container, signatureStartNode);
    const normalizedSuffix = match.suffixStartNode
        ? getHtmlFromNode(match.container, match.suffixStartNode)
        : '';

    return `${prefixHtml}${signature}${normalizedSuffix}${suffix}`;
};

const appendClonedNodes = (container, nodes) => {
    for (const node of nodes) {
        container.appendChild(node.cloneNode(true));
    }
};

const buildHtmlFromNodes = (nodes) => {
    const container = document.createElement('div');
    appendClonedNodes(container, nodes);

    return container.innerHTML;
};

const getMatchedSignatureRange = (
    bodyNodeTexts,
    signatureNodeTexts,
    startIndex,
    minimumMatches
) => {
    let bodyIndex = startIndex;
    let signatureIndex = 0;
    let firstMatchedIndex = -1;

    while (bodyIndex < bodyNodeTexts.length && signatureIndex < signatureNodeTexts.length) {
        const bodyText = bodyNodeTexts[bodyIndex];

        if (!bodyText) {
            bodyIndex += 1;
            continue;
        }

        if (bodyText !== signatureNodeTexts[signatureIndex]) {
            break;
        }

        if (firstMatchedIndex === -1) {
            firstMatchedIndex = bodyIndex;
        }

        bodyIndex += 1;
        signatureIndex += 1;
    }

    if (signatureIndex === signatureNodeTexts.length) {
        return firstMatchedIndex;
    }

    if (firstMatchedIndex !== -1 && signatureIndex >= minimumMatches) {
        return firstMatchedIndex;
    }

    return -1;
};

const splitBodyAtSuffix = (body, suffixPatterns = []) => {
    const patterns = suffixPatterns.length
        ? suffixPatterns
        : [
              '<div data-composer-separator="true">',
              '<div class="unsubscribe-link" data-composer-unsubscribe="true"',
              'data-composer-unsubscribe="true"',
              '<div class="unsubscribe-link"',
              '<div class="original-communication" data-composer-original-communication="true">',
              'data-composer-original-communication="true"',
              '<div class="original-communication"',
          ];
    const indexes = patterns
        .map((pattern) => body.indexOf(pattern))
        .filter((index) => index !== -1)
        .sort((left, right) => left - right);
    const suffixIndex = indexes.length ? indexes[0] : -1;

    return {
        bodyWithoutSuffix: suffixIndex === -1 ? body : body.slice(0, suffixIndex),
        suffix: suffixIndex === -1 ? '' : body.slice(suffixIndex),
    };
};

const signatureVariantsForLocale = ({
    locale,
    previousSignature = '',
    signature = '',
    users = [],
    currentUser = null,
    authUser = null,
}) => {
    const candidates = [
        previousSignature,
        stripSignatureWrapper(previousSignature),
        signature,
        stripSignatureWrapper(signature),
        ...users.map((user) => getContentEmailSignature(user?.email_config, locale) ?? ''),
        getContentEmailSignature(currentUser?.email_config, locale) ?? '',
        getContentEmailSignature(authUser?.email_config, locale) ?? '',
    ];

    return [...new Set(candidates.map((value) => normalizeHtmlText(value)).filter(Boolean))];
};

/** Pick the first separator whose tail contains all signature images in order with no visible text between them. */
const findSeparatorWithSignatureImages = (separatorCandidates, body, signatureImageSources) => {
    if (!signatureImageSources.length) {
        return null;
    }

    const normalizedSignatureImageSources = signatureImageSources.map(normalizeImageSource);

    return separatorCandidates.find((candidate) => {
        const tail = body.slice(candidate.index + candidate.pattern.length);
        const container = document.createElement('div');
        container.innerHTML = tail;

        const tailImages = Array.from(container.querySelectorAll('img')).filter(
            (image) => !isElementOrAncestorHidden(image)
        );

        const startIndex = tailImages.findIndex(
            (image) => getComparableImageSource(image) === normalizedSignatureImageSources[0]
        );

        if (startIndex === -1) {
            return false;
        }

        const candidateImages = tailImages.slice(
            startIndex,
            startIndex + normalizedSignatureImageSources.length
        );

        const hasAllInOrder = normalizedSignatureImageSources.every(
            (source, index) =>
                candidateImages[index] &&
                getComparableImageSource(candidateImages[index]) === source
        );

        if (!hasAllInOrder) {
            return false;
        }

        if (candidateImages.length > 1) {
            const firstImage = candidateImages[0];
            const lastImage = candidateImages[candidateImages.length - 1];
            const textBetween = getNodesBetween(firstImage, lastImage, container)
                .map(getVisibleText)
                .join('')
                .trim();

            if (textBetween) {
                return false;
            }
        }

        return true;
    });
};

const findSignatureSeparatorIndex = ({
    body,
    separatorPatterns,
    locale = 'fr',
    previousSignature = '',
    signature = '',
    users = [],
    currentUser = null,
    authUser = null,
}) => {
    const variants = signatureVariantsForLocale({
        locale,
        previousSignature,
        signature,
        users,
        currentUser,
        authUser,
    });
    const separatorCandidates = separatorPatterns
        .flatMap((pattern) => {
            const indexes = [];
            let searchIndex = body.indexOf(pattern);

            while (searchIndex !== -1) {
                indexes.push({
                    pattern,
                    index: searchIndex,
                });
                searchIndex = body.indexOf(pattern, searchIndex + pattern.length);
            }

            return indexes;
        })
        .sort((left, right) => left.index - right.index);

    if (!separatorCandidates.length) {
        return null;
    }

    if (!variants.length) {
        const signatureImageSources = getImageSources(signature);

        if (!signatureImageSources.length) {
            return separatorCandidates.at(-1);
        }

        return findSeparatorWithSignatureImages(separatorCandidates, body, signatureImageSources);
    }

    const matchingCandidate = separatorCandidates.find((candidate) => {
        const tail = body.slice(candidate.index + candidate.pattern.length);
        const normalizedTail = normalizeHtmlText(tail);

        return variants.some(
            (signatureVariant) => signatureVariant && normalizedTail.includes(signatureVariant)
        );
    });

    return matchingCandidate || separatorCandidates.at(-1);
};

const findSignatureStartIndexFromPreviousSignature = (nodes, previousSignature = '') => {
    const strippedPreviousSignature = stripSignatureWrapper(previousSignature);

    if (!strippedPreviousSignature) {
        return -1;
    }

    const signatureContainer = document.createElement('div');
    signatureContainer.innerHTML = strippedPreviousSignature;

    const signatureNodeTexts = Array.from(signatureContainer.childNodes)
        .map((node) => normalizeHtmlText(node.textContent || ''))
        .filter(Boolean);

    if (!signatureNodeTexts.length) {
        return -1;
    }

    const bodyNodeTexts = nodes.map((node) => normalizeHtmlText(node.textContent || ''));
    const minimumMatches = Math.min(signatureNodeTexts.length, 2);

    for (let startIndex = 0; startIndex < nodes.length; startIndex += 1) {
        const matchedIndex = getMatchedSignatureRange(
            bodyNodeTexts,
            signatureNodeTexts,
            startIndex,
            minimumMatches
        );

        if (matchedIndex !== -1) {
            return matchedIndex;
        }
    }

    return -1;
};

const replaceSignatureUsingTailMatch = ({
    body,
    signature,
    locale,
    previousSignature = '',
    users = [],
    currentUser = null,
    authUser = null,
}) => {
    const { bodyWithoutSuffix, suffix } = splitBodyAtSuffix(body);
    const container = document.createElement('div');
    container.innerHTML = bodyWithoutSuffix;

    const nodes = Array.from(container.childNodes);
    const variants = signatureVariantsForLocale({
        locale,
        previousSignature,
        signature,
        users,
        currentUser,
        authUser,
    });

    if (!variants.length || !nodes.length) {
        return body;
    }

    const previousSignatureStartIndex = findSignatureStartIndexFromPreviousSignature(
        nodes,
        previousSignature
    );

    if (previousSignatureStartIndex !== -1) {
        const signatureStartIndex = includeLeadingSignatureImages(
            nodes,
            previousSignatureStartIndex,
            previousSignature
        );
        const prefixHtml = buildHtmlFromNodes(nodes.slice(0, signatureStartIndex));

        return `${prefixHtml}${signature}${suffix}`;
    }

    for (let index = nodes.length - 1; index >= 0; index -= 1) {
        const normalizedTail = normalizeHtmlText(buildHtmlFromNodes(nodes.slice(index)));
        const hasMatchingSignature = variants.some(
            (variant) => variant && normalizedTail.includes(variant)
        );

        if (!hasMatchingSignature) {
            continue;
        }

        const signatureStartIndex = includeLeadingSignatureImages(nodes, index, signature);
        const prefixHtml = buildHtmlFromNodes(nodes.slice(0, signatureStartIndex));

        return `${prefixHtml}${signature}${suffix}`;
    }

    return body;
};

/** Replace the first managed signature wrapper and remove any duplicates. */
const replaceWrappedSignatureNodes = (body, signature) => {
    const { bodyWithoutSuffix, suffix } = splitBodyAtSuffix(body);
    const container = document.createElement('div');
    container.innerHTML = bodyWithoutSuffix;

    const wrappedSignatures = Array.from(
        container.querySelectorAll('[data-composer-signature="true"], div.signature')
    );

    if (!wrappedSignatures.length) {
        return body;
    }

    wrappedSignatures[0].outerHTML = signature;

    for (const wrappedSignature of wrappedSignatures.slice(1)) {
        if (wrappedSignature.isConnected) {
            wrappedSignature.remove();
        }
    }

    return `${container.innerHTML}${suffix}`;
};

const replaceSignatureWithoutSeparator = ({
    body,
    bodyWithoutSuffix,
    suffix,
    oldSignature,
    newSignature,
}) => {
    const strippedPreviousSignature = stripSignatureWrapper(oldSignature);
    const strippedSignature = stripSignatureWrapper(newSignature);

    if (strippedPreviousSignature && body.includes(strippedPreviousSignature)) {
        return body.replaceAll(strippedPreviousSignature, strippedSignature);
    }

    if (newSignature) {
        const hasSignatureAttribute = bodyWithoutSuffix.includes('data-composer-signature');
        const normalizedBody = normalizeHtmlText(bodyWithoutSuffix);
        const normalizedSignature = normalizeHtmlText(newSignature);
        const hasSignatureContent =
            normalizedSignature && normalizedBody.includes(normalizedSignature);
        const hasSignatureMedia = bodyIncludesSignatureMedia(bodyWithoutSuffix, newSignature);

        if (!hasSignatureAttribute && !hasSignatureContent && !hasSignatureMedia) {
            return `${bodyWithoutSuffix}${newSignature}${suffix}`;
        }
    }

    return body;
};

export const replaceSignature = ({
    body,
    newSignature,
    oldSignature = '',
    locale = 'fr',
    suffixPatterns = [],
    users = [],
    currentUser = null,
    authUser = null,
    skipInsertionWhenAbsent = false,
}) => {
    const bodyWithWrappedSignatureReplaced = replaceWrappedSignatureNodes(body, newSignature);

    if (bodyWithWrappedSignatureReplaced !== body) {
        body = bodyWithWrappedSignatureReplaced;
    }

    if (oldSignature && body.includes(oldSignature)) {
        return body.replaceAll(oldSignature, newSignature);
    }

    const tailMatchedBody = replaceSignatureUsingTailMatch({
        body,
        signature: newSignature,
        locale,
        previousSignature: oldSignature,
        users,
        currentUser,
        authUser,
    });

    if (tailMatchedBody !== body) {
        return tailMatchedBody;
    }

    const mediaMatchedBody = replaceSignatureUsingMediaTailMatch({
        body,
        signature: newSignature,
    });

    if (mediaMatchedBody !== body) {
        return mediaMatchedBody;
    }

    // No managed wrapper, matching text, or matching media remains in the body, which means
    // the user manually removed the signature. Skip the insertion fallbacks so we do not
    // re-add a signature the user deliberately deleted (ACTVX-373). The dedup/replace stages
    // above still run first, so an existing signature is never duplicated (ASPD-29599).
    if (skipInsertionWhenAbsent) {
        return body;
    }

    const { bodyWithoutSuffix, suffix } = splitBodyAtSuffix(body, suffixPatterns);
    // Fallback for cases where the signature wrapper/data attribute was lost after editing or
    // Tiptap normalization and we need to find the visual break before the signature content.
    const separatorPatterns = [
        '<p><br><br></p>',
        '<p><br></p><p><br></p>',
        '<p></p><p></p>',
        '<p><br></p>',
        '<p></p>',
    ];
    const separatorCandidate = findSignatureSeparatorIndex({
        body: bodyWithoutSuffix,
        separatorPatterns,
        locale,
        previousSignature: oldSignature,
        signature: newSignature,
        users,
        currentUser,
        authUser,
    });

    if (!separatorCandidate) {
        return replaceSignatureWithoutSeparator({
            body,
            bodyWithoutSuffix,
            suffix,
            oldSignature,
            newSignature,
        });
    }

    const separatorIndex = separatorCandidate.index + separatorCandidate.pattern.length;
    const bodyPrefix = bodyWithoutSuffix.slice(0, separatorIndex);
    const replacedBody = `${bodyPrefix}${newSignature}${suffix}`;

    return replaceSignatureUsingTailMatch({
        body: replacedBody,
        signature: newSignature,
        locale,
        previousSignature: oldSignature,
        users,
        currentUser,
        authUser,
    });
};

/**
 * Reports whether the body still contains the signature, using the same detection strategies as
 * `replaceSignature`: the managed wrapper marker, the normalized signature text, or the signature
 * image sources. Lets the composer notice when the user manually removed their signature even
 * after Tiptap strips the wrapper attributes (ACTVX-373).
 */
export const bodyContainsSignature = (body, signature) => {
    if (!body || !signature) {
        return false;
    }

    const { bodyWithoutSuffix } = splitBodyAtSuffix(body);

    if (bodyWithoutSuffix.includes('data-composer-signature')) {
        return true;
    }

    const normalizedSignature = normalizeHtmlText(signature);

    if (normalizedSignature && normalizeHtmlText(bodyWithoutSuffix).includes(normalizedSignature)) {
        return true;
    }

    return bodyIncludesSignatureMedia(bodyWithoutSuffix, signature);
};
